// src/crm/ugc/MessagesTab.tsx
//
// Onglet « Messages » du programme UGC : le chat avec l'équipe que chaque créateur a dans son espace
// (en dehors de toute vidéo). À gauche, « Écrire à un membre » (ouvrir le chat de quelqu'un qui n'a
// encore rien écrit) puis la liste des créateurs dont le chat a au moins un message, ceux qui ont
// des messages non lus en tête, avec le dernier message, sa date et le nombre de non-lus. À droite,
// le chat : la personne (état, choix d'e-mails, lien vers sa fiche), le fil à largeur de lecture
// avec « Nouveau » sur ses messages pas encore lus et « Lu » ou « Pas encore lu » sous notre dernier
// message, puis la zone de réponse. Sur très grand écran,
// la fiche passe dans une colonne à droite du fil.
// Comme pour les vidéos, seul un chat choisi est marqué lu (clic dans la liste, même sur celui déjà
// affiché ; lien d'une alerte `?member=` ; choix dans « Écrire à un membre ») : celui que l'onglet
// ouvre d'office garde ses non-lus. La liste et le chat ouvert se relisent toutes les 30 s tant que
// l'onglet est visible, sans rien marquer ni toucher à la réponse en cours (hooks.ts).

import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { UGC_RULES } from '../../../functions/lib/ugc/rules'
import { ApiClientError } from '../../lib/api'
import type { AdminChatListItem, AdminChatThread, UgcChatMessage } from '../../lib/ugcTypes'
import { formatAgo, formatDateTime, MEMBER_STATUS_LABELS } from './format'
import { useSendChatMessage, useUgcChat, useUgcChats, useUgcMembers } from './hooks'
import { ReadMark, lastTeamKey, sentText } from './VideoThread'

/** Heure courante, relue chaque minute (ancienneté des derniers messages). */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000)
    return () => window.clearInterval(timer)
  }, [])
  return now
}

function unreadLabel(count: number): string {
  return count === 1 ? '1 non lu' : `${count} non lus`
}

/** Dernier message d'une conversation, sur une ligne : « L'équipe : … » quand c'est nous. */
function lastLine(item: AdminChatListItem): string {
  const text = item.lastMessage.body.replace(/\s+/g, ' ').trim()
  return item.lastMessage.author === 'team' ? `L'équipe : ${text}` : text
}

/** Membres à qui l'équipe peut écrire (accès à leur espace), par nom. */
function WriteTo({ onPick }: { onPick: (memberId: string) => void }) {
  const members = useUgcMembers()
  const fieldId = useId()
  const writable = (members.data ?? [])
    .filter((m) => m.status === 'active' || m.status === 'paused')
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'fr'))
  return (
    <div className="ugc-select-field ugc-chat-start">
      <label htmlFor={fieldId}>Écrire à un membre</label>
      <select
        id={fieldId}
        className="ugc-select"
        value=""
        onChange={(event) => {
          if (event.target.value) onPick(event.target.value)
        }}
      >
        <option value="">{members.isPending ? 'Chargement…' : 'Choisir…'}</option>
        {writable.map((m) => (
          <option key={m.id} value={m.id}>
            {m.displayName || m.email}
          </option>
        ))}
      </select>
    </div>
  )
}

function ChatList({ items, currentId, now, onSelect }: {
  items: readonly AdminChatListItem[]
  currentId: string | null
  now: Date
  onSelect: (memberId: string) => void
}) {
  return (
    <ul className="ugc-queue ugc-chat-list" aria-label="Conversations">
      {items.map((item) => (
        <li key={item.memberId}>
          <button
            type="button"
            className="ugc-queue-item"
            aria-current={item.memberId === currentId ? 'true' : undefined}
            onClick={() => onSelect(item.memberId)}
          >
            <span className="ugc-chat-list-head">
              <span className="ugc-queue-title">{item.memberName || 'Sans nom'}</span>
              <span className="ugc-queue-when">{formatAgo(item.lastMessage.createdAt, now)}</span>
            </span>
            <span className="ugc-chat-excerpt">{lastLine(item)}</span>
            {item.unread > 0 && <span className="ugc-badge ugc-badge--warn">{unreadLabel(item.unread)}</span>}
          </button>
        </li>
      ))}
    </ul>
  )
}

function ChatMessages({ thread, readBefore }: { thread: AdminChatThread; readBefore: string | null }) {
  const author = thread.memberName || 'La personne'
  const isNew = (m: UgcChatMessage) => m.author === 'member' && (readBefore === null || m.createdAt > readBefore)
  const readMarkId = lastTeamKey(thread.messages, (m) => m.id)
  if (thread.messages.length === 0) {
    return (
      <p className="ugc-muted ugc-chat-empty">
        {`Aucun message pour le moment. Écris le premier : ${thread.memberName || 'la personne'} le lira dans son espace.`}
      </p>
    )
  }
  return (
    <ol className="ugc-thread ugc-chat-thread" aria-label="Fil du chat">
      {thread.messages.map((m) => (
        <li key={m.id} className={`ugc-msg ugc-msg--${m.author}`}>
          <span className="ugc-msg-meta">
            <span className="ugc-msg-author">{m.author === 'team' ? "L'équipe" : author}</span>
            <span>{formatDateTime(m.createdAt)}</span>
            {isNew(m) && <span className="ugc-badge ugc-badge--warn">Nouveau</span>}
          </span>
          <span className="ugc-prose">{m.body}</span>
          {m.id === readMarkId && <ReadMark at={m.createdAt} memberReadAt={thread.memberReadAt ?? null} />}
        </li>
      ))}
    </ol>
  )
}

function sendErrorText(error: Error): string {
  if (!(error instanceof ApiClientError)) {
    return "Le message n'a pas pu partir (réseau indisponible). Réessaie : s'il était déjà parti, il est dans le chat."
  }
  if (error.status === 404) return "Ce membre n'existe plus : ses données ont peut-être été effacées."
  if (error.code === 'network_error') return `Le message n'a pas été enregistré (erreur ${error.status}). Réessaie dans un instant.`
  return error.message
}

type Outcome = { ok: boolean; text: string }

/**
 * Réponse de l'équipe dans le chat. Pas de maxLength : il tronquerait un texte collé sans rien dire ;
 * le serveur applique le schéma des messages, son refus s'affiche et le texte reste dans le champ.
 */
function ChatReplyForm({ memberId, memberName }: { memberId: string; memberName: string }) {
  const send = useSendChatMessage()
  const [body, setBody] = useState('')
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const fieldId = useId()
  const helpId = useId()
  const empty = body.trim() === ''

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (empty || send.isPending) return
    setOutcome(null)
    send.mutate(
      { memberId, body: body.trim() },
      {
        onSuccess: ({ email }) => {
          setBody('')
          setOutcome({ ok: true, text: sentText(email, memberName) })
        },
        onError: (error) => setOutcome({ ok: false, text: sendErrorText(error) }),
      },
    )
  }

  return (
    <form className="ugc-inline-form ugc-chat-reply" onSubmit={submit}>
      <label htmlFor={fieldId}>{memberName ? `Ton message à ${memberName}` : 'Ton message'}</label>
      <textarea
        id={fieldId}
        className="ugc-textarea"
        rows={3}
        value={body}
        aria-describedby={helpId}
        onChange={(e) => setBody(e.target.value)}
      />
      <p id={helpId} className="ugc-muted">
        {`La personne le lit dans le chat de son espace. Un e-mail la prévient, au plus un toutes les ${UGC_RULES.messageEmailWindowMinutes} minutes, ou son résumé du matin si elle l'a choisi. Écris au nom de l'équipe, en « nous ».`}
      </p>
      <div className="ugc-actions">
        <button type="submit" className="ugc-btn ugc-btn--strong" disabled={empty || send.isPending}>
          Envoyer le message
        </button>
      </div>
      {send.isPending && <p className="ugc-muted">Envoi du message…</p>}
      {outcome && (
        <p role={outcome.ok ? 'status' : 'alert'} className={outcome.ok ? 'ugc-notice' : 'ugc-error'}>
          {outcome.text}
        </p>
      )}
    </form>
  )
}

function closedText(thread: AdminChatThread): string {
  return thread.memberStatus === 'invited'
    ? "Cette personne n'a pas encore accepté les conditions : elle n'a pas encore accès à son espace."
    : 'Cette personne a quitté le programme : le chat est fermé.'
}

/** Chat chargé : la lecture d'avant la première ouverture de la visite place « Nouveau » jusqu'au bout. */
function LoadedChat({ thread }: { thread: AdminChatThread }) {
  const [readBefore] = useState(thread.lastReadAt)
  return (
    <div className="ugc-chat-panel">
      <div className="ugc-chat-conversation">
        <ChatMessages thread={thread} readBefore={readBefore} />
        {thread.canWrite ? (
          <ChatReplyForm memberId={thread.memberId} memberName={thread.memberName} />
        ) : (
          <p className="ugc-muted">{closedText(thread)}</p>
        )}
      </div>
      <dl className="ugc-facts ugc-chat-facts">
        <div>
          <dt>État</dt>
          <dd>{MEMBER_STATUS_LABELS[thread.memberStatus]}</dd>
        </div>
        <div>
          <dt>E-mails de nos messages</dt>
          <dd>{thread.emailDigest === 'daily' ? 'Un résumé chaque matin' : 'Un e-mail à chaque message'}</dd>
        </div>
        <div>
          <dt>Fiche</dt>
          <dd>
            <Link className="ugc-link" to={`/crm/contacts/${encodeURIComponent(thread.creatorId)}`}>
              Voir la fiche
            </Link>
          </dd>
        </div>
      </dl>
    </div>
  )
}

/**
 * Chat d'un membre et zone de réponse. `markRead` : le membre a été choisi, ses lectures marquent le
 * chat lu ; `readRequest` change à chaque nouveau clic sur lui, qui relit le chat en le marquant.
 */
function MemberChat({ memberId, markRead, readRequest }: { memberId: string; markRead: boolean; readRequest: number }) {
  const chat = useUgcChat(memberId, { markRead })
  // Relecture qui marque lu, gardée à jour à chaque rendu : seul un nouveau clic (`readRequest`) la lance.
  const refetchRead = useRef(chat.refetchRead)
  refetchRead.current = chat.refetchRead
  const headingId = useId()
  const seenRequest = useRef(readRequest)
  useEffect(() => {
    if (readRequest === seenRequest.current) return
    seenRequest.current = readRequest
    void refetchRead.current()
  }, [readRequest])
  return (
    <section className="ugc-chat-detail" aria-labelledby={headingId}>
      <h2 id={headingId} className="ugc-video-title">
        {chat.data ? chat.data.memberName || 'Sans nom' : 'Chat'}
      </h2>
      {chat.data ? (
        <LoadedChat key={chat.data.memberId} thread={chat.data} />
      ) : chat.isError ? (
        <p className="ugc-error">{`Le chat n'a pas pu être chargé : ${chat.error.message}`}</p>
      ) : (
        <p className="ugc-muted">Chargement du chat…</p>
      )}
    </section>
  )
}

export function MessagesTab() {
  // Lien d'une alerte : le chat de ce membre s'ouvre, choisi (donc marqué lu).
  const [searchParams] = useSearchParams()
  const linkedId = searchParams.get('member')
  const [selectedId, setSelectedId] = useState<string | null>(() => linkedId)
  const [chosenId, setChosenId] = useState<string | null>(() => linkedId)
  const [readRequest, setReadRequest] = useState(0)
  const now = useNow()
  const chats = useUgcChats()
  const items = chats.data ?? []
  // Sans choix, la première conversation de la liste (non-lus en tête) est ouverte, sans être lue,
  // puis figée : la liste qui se relit et se réordonne ne change pas le chat affiché (ni la réponse
  // en cours). `chosenId` n'est pas touché : ce chat reste lu sans `read=1`.
  const firstId = items[0]?.memberId ?? null
  useEffect(() => {
    if (selectedId === null && firstId !== null) setSelectedId(firstId)
  }, [selectedId, firstId])
  const currentId = selectedId ?? firstId

  function select(memberId: string) {
    setSelectedId(memberId)
    setChosenId(memberId)
    setReadRequest((n) => n + 1)
  }

  return (
    <section className="ugc-tab-panel" aria-label="Messages">
      <div className="ugc-chats">
        <div className="ugc-queue-col">
          <WriteTo onPick={select} />
          {chats.isPending && <p className="ugc-muted">Chargement…</p>}
          {chats.isError && !chats.data && <p className="ugc-error">{`Les conversations n'ont pas pu être chargées : ${chats.error.message}`}</p>}
          {chats.isSuccess && items.length === 0 && <p className="ugc-empty">Personne ne nous a encore écrit.</p>}
          {items.length > 0 && <ChatList items={items} currentId={currentId} now={now} onSelect={select} />}
        </div>
        <div className="ugc-video-col">
          {currentId && (
            <MemberChat key={currentId} memberId={currentId} markRead={chosenId === currentId} readRequest={readRequest} />
          )}
        </div>
      </div>
    </section>
  )
}
